import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import ImageUploader from "@/components/admin/ImageUploader";
import {
  PHOTO_SECTION_PRESETS,
  RATING_LABELS,
  slugifyKey,
  type ListingRatings,
  type PhotoSection,
} from "@/lib/listingContent";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

export interface RichContentValue {
  description_full: string;
  why_special: string;
  about_location: string;
  buyer_profile: string;
  differentials: string;
  institutional_text: string;
  ratings: ListingRatings;
  photo_sections: PhotoSection[];
  image_hero: string;
  image_card: string;
  image_og: string;
  image_thumbnail: string;
  video_url: string;
  drone_url: string;
  virtual_tour_url: string;
  floorplan_url: string;
  pdf_url: string;
}

export const EMPTY_RICH_CONTENT: RichContentValue = {
  description_full: "",
  why_special: "",
  about_location: "",
  buyer_profile: "",
  differentials: "",
  institutional_text: "",
  ratings: {},
  photo_sections: [],
  image_hero: "",
  image_card: "",
  image_og: "",
  image_thumbnail: "",
  video_url: "",
  drone_url: "",
  virtual_tour_url: "",
  floorplan_url: "",
  pdf_url: "",
};

interface Props {
  value: RichContentValue;
  onChange: (next: RichContentValue) => void;
}

const RichContentForm = ({ value, onChange }: Props) => {
  const patch = (p: Partial<RichContentValue>) => onChange({ ...value, ...p });

  const setRating = (k: keyof ListingRatings, n: number) =>
    patch({ ratings: { ...value.ratings, [k]: n } });

  const addSection = (preset?: string) => {
    const title = preset || "Nova seção";
    const key = slugifyKey(`${title}-${value.photo_sections.length + 1}`);
    patch({ photo_sections: [...value.photo_sections, { key, title, images: [] }] });
  };

  const updateSection = (i: number, next: Partial<PhotoSection>) => {
    const list = value.photo_sections.slice();
    list[i] = { ...list[i], ...next };
    patch({ photo_sections: list });
  };

  const removeSection = (i: number) => {
    patch({ photo_sections: value.photo_sections.filter((_, idx) => idx !== i) });
  };

  const moveSection = (i: number, dir: -1 | 1) => {
    const list = value.photo_sections.slice();
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    patch({ photo_sections: list });
  };

  const usedPresets = new Set(value.photo_sections.map((s) => s.title));
  const availablePresets = PHOTO_SECTION_PRESETS.filter((p) => !usedPresets.has(p));

  return (
    <details className="border border-border rounded-md">
      <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
        📝 Conteúdo rico, avaliação, galeria por ambiente e mídias (opcional)
      </summary>
      <div className="p-3 space-y-4">
        {/* Rich narrative fields */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-foreground font-body">Textos longos</p>
          <Textarea placeholder="Descrição completa (aparece na aba 'Sobre este imóvel')" value={value.description_full} onChange={(e) => patch({ description_full: e.target.value })} rows={4} className="font-body" />
          <Textarea placeholder="Por que este imóvel é especial" value={value.why_special} onChange={(e) => patch({ why_special: e.target.value })} rows={3} className="font-body" />
          <Textarea placeholder="Sobre a localização" value={value.about_location} onChange={(e) => patch({ about_location: e.target.value })} rows={3} className="font-body" />
          <Textarea placeholder="Perfil do comprador" value={value.buyer_profile} onChange={(e) => patch({ buyer_profile: e.target.value })} rows={2} className="font-body" />
          <Textarea placeholder="Diferenciais" value={value.differentials} onChange={(e) => patch({ differentials: e.target.value })} rows={3} className="font-body" />
          <Textarea placeholder="Texto institucional" value={value.institutional_text} onChange={(e) => patch({ institutional_text: e.target.value })} rows={2} className="font-body" />
        </div>

        {/* Ratings */}
        <div className="border-t border-border pt-3">
          <p className="text-xs font-semibold text-foreground font-body mb-2">Avaliação do imóvel (0 a 5)</p>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(RATING_LABELS) as (keyof ListingRatings)[]).map((k) => (
              <div key={k}>
                <label className="text-xs text-muted-foreground font-body block mb-1">{RATING_LABELS[k]}</label>
                <Input
                  type="number"
                  min={0}
                  max={5}
                  step={0.5}
                  value={value.ratings[k] ?? ""}
                  onChange={(e) => setRating(k, e.target.value === "" ? 0 : Number(e.target.value))}
                  className="font-body"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Photo sections */}
        <div className="border-t border-border pt-3">
          <p className="text-xs font-semibold text-foreground font-body mb-2">Galeria por ambiente</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {availablePresets.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => addSection(p)}
                className="text-xs font-body px-2 py-0.5 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                + {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => addSection()}
              className="text-xs font-body px-2 py-0.5 rounded-full border border-dashed border-border hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" /> personalizada
            </button>
          </div>

          <div className="space-y-3">
            {value.photo_sections.map((sec, i) => (
              <div key={sec.key} className="border border-border rounded-md p-2 space-y-2 bg-muted/30">
                <div className="flex items-center gap-1">
                  <Input
                    value={sec.title}
                    onChange={(e) => updateSection(i, { title: e.target.value })}
                    placeholder="Título da seção"
                    className="font-body font-semibold text-sm"
                    maxLength={60}
                  />
                  <Button type="button" size="icon" variant="ghost" onClick={() => moveSection(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => moveSection(i, 1)} disabled={i === value.photo_sections.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeSection(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <ImageUploader
                  images={sec.images}
                  onChange={(imgs) => updateSection(i, { images: imgs })}
                />
              </div>
            ))}
            {value.photo_sections.length === 0 && (
              <p className="text-xs text-muted-foreground font-body">Nenhuma seção adicionada. Clique nos botões acima para incluir ambientes.</p>
            )}
          </div>
        </div>

        {/* Extra media */}
        <div className="border-t border-border pt-3 space-y-2">
          <p className="text-xs font-semibold text-foreground font-body">Mídias extras (URLs)</p>
          <Input placeholder="Imagem Hero (usa esta em vez da 1ª foto)" value={value.image_hero} onChange={(e) => patch({ image_hero: e.target.value })} className="font-body" />
          <Input placeholder="Imagem do Card (grid de listagem)" value={value.image_card} onChange={(e) => patch({ image_card: e.target.value })} className="font-body" />
          <Input placeholder="Imagem OG (compartilhamento)" value={value.image_og} onChange={(e) => patch({ image_og: e.target.value })} className="font-body" />
          <Input placeholder="Thumbnail" value={value.image_thumbnail} onChange={(e) => patch({ image_thumbnail: e.target.value })} className="font-body" />
          <Input placeholder="Vídeo Drone (URL YouTube ou MP4)" value={value.drone_url} onChange={(e) => patch({ drone_url: e.target.value })} className="font-body" />
          <p className="text-[10px] text-muted-foreground font-body">Dica: o vídeo tour principal do imóvel é definido acima em "Vídeo".</p>
          <Input placeholder="Tour Virtual (URL Matterport, Kuula, etc.)" value={value.virtual_tour_url} onChange={(e) => patch({ virtual_tour_url: e.target.value })} className="font-body" />
          <Input placeholder="Planta baixa (URL da imagem)" value={value.floorplan_url} onChange={(e) => patch({ floorplan_url: e.target.value })} className="font-body" />
          <Input placeholder="PDF de apresentação (URL)" value={value.pdf_url} onChange={(e) => patch({ pdf_url: e.target.value })} className="font-body" />
        </div>
      </div>
    </details>
  );
};

export default RichContentForm;
